import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  MenuItem,
  FormControlLabel,
  Link,
  DialogActions,
  DialogTitle,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import labelScanReviewService, {
  MergeCandidate,
  SlotProbeResult,
  ResolutionCandidate,
  ScanDetail,
  SlotAssignment,
  VerificationClaim,
  VisionSlot,
} from '../../../services/labelScanReviewService';
import wineNameService, { RegionOption, WineColor } from '../../../services/wineNameService';
import { parseVintageInput } from '../../../services/wineService';
import { normalizeDisplayCase } from '../../../utils/displayCase';
import {
  EntityOps, MintTagged, WineIdentityFields, buildDisplayNamePreview, useWineIdentityForm,
} from '../../../components/wine/WineIdentityForm';

/**
 * P5c — THE SCREEN (design §7). Reading order is the epistemic order:
 *
 *   1. the PHOTOGRAPH (what the bottle says)
 *   2. the MACHINE's reading (what the AI thinks it says, and why it did or
 *      didn't resolve — caps in plain language, not codes)
 *   3. the HUMANS' corrections — P5b; until it exists this section renders
 *      NOTHING, not a placeholder
 *   4. the WORLD's testimony (web verification claims with sources)
 *   5. the CANDIDATES (who it might be, and why others were excluded)
 *   6. the DECISION (approve-as-new / merge / reject / rescan)
 *
 * Identity fields are ID-backed pickers over the live resolver's entities —
 * free text can only mint a producer through the deliberate, named
 * "new producer" path, never through a typo.
 */

// ── Plain-language translations ───────────────────────────────────────────────

const AUTHORITY_TEXT: Record<string, string> = {
  structural: 'read from the label’s printed structure',
  positional: 'inferred from where it sits on the label',
  typographic: 'inferred from type styling',
  guess: 'the model’s guess — not confirmed by anything on the label',
  'hierarchy-candidate': 'looked like a known wine term, pending confirmation',
};

const SLOT_LABELS: Record<string, string> = {
  producer: 'Producer',
  appellation: 'Appellation / Region',
  specialName: 'Cuvée / Special name',
  labelGrapes: 'Grapes',
  vintage: 'Vintage',
  association: 'Producer + wine',
};

/** The caps, spoken (design §7: plain language, never codes). */
function capSentences(sa?: SlotAssignment): string[] {
  if (!sa) return [];
  const out: string[] = [];
  if (sa.guessedSlots?.length) {
    out.push(`The model itself marked ${sa.guessedSlots.join(', ')} as a guess, which caps how far the verdict can go.`);
  }
  if (sa.unconfirmedSlots?.length) {
    out.push(`${sa.unconfirmedSlots.join(', ')} looked like a known wine term but could not be confirmed against the region tree.`);
  }
  if (sa.capApplied && sa.verdictBeforeCap) {
    out.push(`Without these caps the verdict would have been “${sa.verdictBeforeCap}”.`);
  }
  if (sa.regionDiscriminated) {
    out.push('The label’s region ruled out a same-name producer from somewhere else.');
  }
  if (sa.discriminatorCapped) {
    out.push('Because that narrowing did real work, the verdict is held at “proposed” for a human to confirm.');
  }
  if (sa.thinMatchDemoted) {
    out.push('A match resting on a single identity component was demoted — one component is too thin to call it matched.');
  }
  if (sa.unsupportedByTranscription?.length) {
    out.push(`These values do not appear in the model’s own transcription of the label: ${sa.unsupportedByTranscription.join(', ')} — treat them with suspicion.`);
  }
  return out;
}

/** §13.6 tier badges — plain words, ordered by authority. */
const TIER_LABEL: Record<string, string> = {
  'tech-sheet': 'producer’s sheet',
  'producer-site': 'producer’s site',
  trade: 'trade',
  merchant: 'merchant',
};
const TIER_RANK: Record<string, number> = { 'tech-sheet': 3, 'producer-site': 2, trade: 1, merchant: 0 };

const FINDING_LABEL: Record<string, { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' }> = {
  exists: { label: 'found in the world', color: 'success' },
  variant: { label: 'spelling variant of a known name', color: 'info' },
  not_found: { label: 'no public record found', color: 'default' },
  contradiction: { label: 'a source contradicts the label', color: 'error' },
};

// ── Section: the photograph ───────────────────────────────────────────────────

const Photos: React.FC<{ scan: ScanDetail }> = ({ scan }) => {
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>The photograph</Typography>
      <Stack direction="row" spacing={2}>
        {scan.images.map((img) => (
          <Box key={img.s3Key} sx={{ maxWidth: 320 }}>
            <Tooltip title={img.sha256 ? `sha256 ${img.sha256}` : 'no hash recorded'} placement="top">
              <Box
                component="img"
                src={img.url}
                alt={`${img.side} label`}
                onClick={() => setZoomUrl(img.url)}
                sx={{ width: '100%', borderRadius: 1, cursor: 'zoom-in', border: '1px solid', borderColor: 'divider' }}
              />
            </Tooltip>
            <Typography variant="caption" color="text.secondary" display="block">
              {img.side} · {(img.sizeBytes / 1024).toFixed(0)} KB
            </Typography>
            {img.sha256 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {img.sha256}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
      <Dialog open={!!zoomUrl} onClose={() => setZoomUrl(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {zoomUrl && <Box component="img" src={zoomUrl} alt="label, full size" sx={{ maxWidth: '100%', display: 'block' }} />}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

// ── Section: the machine's reading ────────────────────────────────────────────

function slotValue(slot?: VisionSlot): string {
  if (!slot) return '—';
  if (slot.text != null && slot.text !== '') return String(slot.text);
  if (slot.value != null) return String(slot.value);
  if (slot.values?.length) return slot.values.join(', ');
  return '—';
}

const MachineReading: React.FC<{ scan: ScanDetail }> = ({ scan }) => {
  const read = scan.visionRead ?? {};
  const res = scan.resolution ?? {};
  const sa = res.slotAssignment;
  const caps = capSentences(sa);
  const slots = ['producer', 'appellation', 'specialName', 'labelGrapes', 'vintage'];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">The machine&apos;s reading</Typography>
        {res.verdict && <Chip size="small" label={res.verdict.replace('_', ' ')} color={res.verdict === 'matched' ? 'success' : res.verdict === 'flagged' ? 'error' : 'info'} />}
        {res.engineVersion && <Typography variant="caption" color="text.secondary">{res.engineVersion}</Typography>}
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Slot</TableCell>
            <TableCell>Value read</TableCell>
            <TableCell>Confidence</TableCell>
            <TableCell>How it was read</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {slots.map((key) => {
            const slot = read[key];
            const capped = sa?.cappedSlots?.includes(key);
            return (
              <TableRow key={key}>
                <TableCell>{SLOT_LABELS[key] ?? key}</TableCell>
                <TableCell sx={{ fontWeight: slot ? 500 : 400 }}>
                  {slotValue(slot)}
                  {capped && <Chip size="small" label="caps the verdict" color="warning" variant="outlined" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{slot?.confidence != null ? slot.confidence.toFixed(2) : '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {slot?.authority ? (AUTHORITY_TEXT[slot.authority] ?? slot.authority) : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {caps.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Why it resolved the way it did</Typography>
          {caps.map((s, i) => <Typography key={i} variant="body2">· {s}</Typography>)}
        </Alert>
      )}
      {(res.visionFlags?.length ?? 0) > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Read-quality notes: {res.visionFlags!.join('; ')}
        </Typography>
      )}
      {(res.flags?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {res.flags!.map((f, i) => (
            <Typography key={i} variant="body2">{f.userMessage || f.detail} <Typography component="span" variant="caption" color="text.secondary">({f.code})</Typography></Typography>
          ))}
        </Alert>
      )}
    </Paper>
  );
};

// ── Section: what the humans said (P5b §5) ───────────────────────────────────

const CORRECTION_KIND_LABEL: Record<string, string> = {
  edit: 'corrected the reading',
  pick_candidate: 'picked a candidate',
  vintage: 'fixed the vintage',
  photo_added: 'added a photo',
};

/**
 * Staff corrections inform the operator; they never touched canon. Renders
 * NOTHING when the scan carries none — the P5c coupling, still honoured.
 */
const StaffCorrections: React.FC<{ scan: ScanDetail }> = ({ scan }) => {
  const corrections = scan.corrections ?? [];
  const acceptance = scan.staffAcceptance;
  if (corrections.length === 0 && !acceptance) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>What the venue said</Typography>
      {acceptance && (
        <Alert severity="info" sx={{ mb: corrections.length ? 1.5 : 0 }}>
          {acceptance.actor?.email ?? 'A staff member'} accepted this scan
          {acceptance.converged ? ' onto a draft another venue had already scanned' : ' as a new draft'}
          {' '}on {new Date(acceptance.at).toLocaleDateString()} — the venue has been using the item since.
        </Alert>
      )}
      {corrections.map((c, i) => (
        <Box key={i} sx={{ mb: 0.75 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={SLOT_LABELS[c.slot] ?? c.slot} />
            <Typography variant="body2">
              {c.from != null && String(c.from) !== '' && (
                <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                  {String(c.from)}
                </Box>
              )}
              {c.to != null && String(c.to) !== '' && (
                <Box component="span" sx={{ fontWeight: 600, ml: c.from != null ? 1 : 0 }}>
                  {String(c.to)}
                </Box>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {CORRECTION_KIND_LABEL[c.kind] ?? c.kind} · {c.actorEmail ?? 'venue staff'} · {new Date(c.at).toLocaleDateString()}
            </Typography>
          </Stack>
        </Box>
      ))}
      {corrections.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Corrections inform this decision; they were never written to the wine database.
        </Typography>
      )}
    </Paper>
  );
};

// ── Section: the world's testimony ────────────────────────────────────────────

const ClaimRow: React.FC<{ claim: VerificationClaim; onUseCanonical?: (slot: string, value: string, sourceUrl?: string) => void }> = ({ claim, onUseCanonical }) => {
  const f = FINDING_LABEL[claim.finding] ?? { label: claim.finding, color: 'default' as const };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{SLOT_LABELS[claim.slot] ?? claim.slot}:</Typography>
        <Typography variant="body2">&ldquo;{claim.labelValue}&rdquo;</Typography>
        <Chip size="small" label={f.label} color={f.color} variant="outlined" />
        {claim.sourceTier && (
          <Chip
            size="small"
            label={TIER_LABEL[claim.sourceTier] ?? claim.sourceTier}
            color={claim.sourceTier === 'tech-sheet' ? 'success' : 'default'}
            variant={claim.sourceTier === 'tech-sheet' ? 'filled' : 'outlined'}
          />
        )}
      </Stack>
      {claim.finding === 'variant' && claim.canonicalSpelling && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Sources print it as <b>{claim.canonicalSpelling}</b>
          </Typography>
          {onUseCanonical && (
            // Item 6: what the machine already looked up is one tap away —
            // never retyped. The first source rides along for the audit eye.
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', py: 0, minWidth: 0 }}
              onClick={() => onUseCanonical(claim.slot, claim.canonicalSpelling!, claim.sources?.[0]?.url)}
            >
              Use &ldquo;{claim.canonicalSpelling}&rdquo;
            </Button>
          )}
          {claim.sources?.[0] && (
            <Link href={claim.sources[0].url} target="_blank" rel="noopener noreferrer" variant="caption">
              source
            </Link>
          )}
        </Stack>
      )}
      {claim.finding === 'not_found' && (
        <Typography variant="caption" color="text.secondary">
          Information, not a strike — small producers and new cuvées are often unindexed.
        </Typography>
      )}
      {claim.note && <Typography variant="body2" color="text.secondary">{claim.note}</Typography>}
      {(claim.sources?.length ?? 0) > 0 && (
        <Stack sx={{ pl: 1, mt: 0.5 }} spacing={0.25}>
          {claim.sources!.map((s, i) => (
            <Tooltip key={i} title={s.quote ?? ''} placement="top-start">
              <Link href={s.url} target="_blank" rel="noopener noreferrer" variant="caption">
                {s.title || s.url}
              </Link>
            </Tooltip>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const WorldTestimony: React.FC<{ scan: ScanDetail; onUseCanonical?: (slot: string, value: string, sourceUrl?: string) => void }> = ({ scan, onUseCanonical }) => {
  const wv = scan.webVerification;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">The world&apos;s testimony</Typography>
        {wv?.model && <Typography variant="caption" color="text.secondary">{wv.model} · {wv.usage?.searches ?? 0} searches</Typography>}
      </Stack>
      {!wv && (
        <Typography variant="body2" color="text.secondary">This scan was not verified (verification runs on proposed, producer-level and flagged scans, plus a sample of matched ones).</Typography>
      )}
      {wv && wv.status !== 'verified' && (
        <Typography variant="body2" color="text.secondary">
          Verification {wv.status}{wv.reason ? ` — ${wv.reason}` : ''}. The reading stands on its own.
        </Typography>
      )}
      {wv?.sheet && (
        <Alert severity={wv.sheet.vintageMatch ? 'success' : 'info'} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {wv.sheet.statement}
            </Typography>
            {wv.sheet.reused && <Chip size="small" label="stored sheet — no new lookup" variant="outlined" />}
            {wv.sheet.url && (
              <Link href={wv.sheet.url} target="_blank" rel="noopener noreferrer" variant="body2">
                View the sheet
              </Link>
            )}
          </Stack>
          {wv.sheet.claims.length > 0 && (
            <Stack sx={{ mt: 0.5 }} spacing={0.25}>
              {wv.sheet.claims.map((c, i) => (
                <Tooltip key={i} title={c.quote} placement="top-start">
                  <Typography variant="caption" color="text.secondary">
                    {c.field}: <b>{typeof c.value === 'object' ? JSON.stringify(c.value) : String(c.value)}</b>
                    {c.scope === 'vintage' ? ' (this vintage)' : ''}
                  </Typography>
                </Tooltip>
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {wv?.status === 'verified' && (wv.claims ?? [])
        .slice()
        .sort((a, b) => (TIER_RANK[b.sourceTier ?? ''] ?? -1) - (TIER_RANK[a.sourceTier ?? ''] ?? -1))
        .map((c, i) => <ClaimRow key={i} claim={c} onUseCanonical={onUseCanonical} />)}
    </Paper>
  );
};

// ── Section: the candidates ───────────────────────────────────────────────────

const CandidateCard: React.FC<{ c: ResolutionCandidate; onPick?: (wineNameId: string, display: string) => void }> = ({ c, onPick }) => {
  const exclusions = c.evidence.filter((e) => e.type === 'exclusion');
  const positive = c.evidence.filter((e) => e.type !== 'exclusion');
  const pickable = Boolean(c.identityRef.wineNameId && onPick);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, ...(pickable && { cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }) }}
      onClick={pickable ? () => onPick!(c.identityRef.wineNameId!, c.display) : undefined}
    >
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.display}</Typography>
        {c.disambiguation?.fullName && c.disambiguation.fullName !== c.display && (
          <Typography variant="caption" color="text.secondary">({c.disambiguation.fullName})</Typography>
        )}
        {c.disambiguation?.country && <Chip size="small" label={c.disambiguation.country} variant="outlined" />}
        <Typography variant="caption" color="text.secondary">confidence {c.confidence.overall.toFixed(2)}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {Object.entries(c.matchDetail).map(([k, v]) => `${k}: ${v}`).join(' · ')}
      </Typography>
      {positive.map((e, i) => (
        <Typography key={i} variant="caption" display="block" color="text.secondary">· {e.detail}</Typography>
      ))}
      {exclusions.map((e, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ color: 'warning.main' }}>
          excluded: {e.detail}
        </Typography>
      ))}
    </Paper>
  );
};

// ── Section: the decision ─────────────────────────────────────────────────────

type ActionKind = 'approve_new' | 'merge' | 'reject' | 'rescan';

export interface CanonicalUse {
  slot: string;
  value: string;
  sourceUrl?: string;
  /** Monotonic — a repeat tap on the same value still applies. */
  n: number;
}

export interface CandidateUse {
  wineNameId: string;
  display: string;
  n: number;
}

interface DecisionPanelProps {
  scan: ScanDetail;
  onDecided: () => void;
  canonicalUse?: CanonicalUse | null;
  candidateUse?: CandidateUse | null;
}

/**
 * Item 7 — the unresolved slot asks the web about ITSELF: one targeted
 * Haiku question, on demand, answer shown with sources and its cost, and a
 * Create action that carries the scan (and thus this probe) as evidence.
 * The web proposes and evidences; only the operator creates.
 */
const AskWebPanel: React.FC<{
  scanId: string;
  slot: 'region' | 'producer' | 'grape';
  name: string;
  onCreateWithEvidence: (name: string, hints?: { country?: string | null; parent?: string | null }) => void;
}> = ({ scanId, slot, name, onCreateWithEvidence }) => {
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<SlotProbeResult | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);

  const ask = async () => {
    setProbing(true);
    setProbeError(null);
    try {
      setProbe(await labelScanReviewService.slotProbe(scanId, { slot, name }));
    } catch (e: any) {
      setProbeError(e?.response?.data?.message || 'The web probe failed.');
    } finally {
      setProbing(false);
    }
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      {!probe && (
        <Button size="small" onClick={ask} disabled={probing || !name.trim()} sx={{ textTransform: 'none' }}>
          {probing ? 'Asking the web…' : `Ask the web about “${name}”`}
        </Button>
      )}
      {probeError && <Alert severity="warning" sx={{ mt: 0.5 }}>{probeError}</Alert>}
      {probe && (
        <Alert severity={probe.finding === 'known' ? 'success' : 'info'} sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            {probe.summary ?? (probe.finding === 'unknown' ? 'No public record found — information, not a strike.' : '')}
            {probe.canonicalName && <> Canonical: <b>{probe.canonicalName}</b>.</>}
            {probe.country && <> Country: {probe.country}.</>}
            {probe.parent && <> Within: {probe.parent}.</>}
          </Typography>
          <Stack sx={{ mt: 0.5 }} spacing={0.25}>
            {probe.sources.map((src, i) => (
              <Tooltip key={i} title={src.quote ?? ''} placement="top-start">
                <Link href={src.url} target="_blank" rel="noopener noreferrer" variant="caption">
                  {src.title || src.url}
                </Link>
              </Tooltip>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            {probe.finding !== 'unknown' && (
              <Button
                size="small" variant="outlined" sx={{ textTransform: 'none' }}
                onClick={() => onCreateWithEvidence(probe.canonicalName || name, { country: probe.country, parent: probe.parent })}
              >
                Create “{probe.canonicalName || name}” with this evidence
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              {probe.usage.searches} search{probe.usage.searches === 1 ? '' : 'es'}
              {probe.costUsd != null && ` · $${probe.costUsd.toFixed(4)}`}
            </Typography>
          </Stack>
        </Alert>
      )}
    </Box>
  );
};

/**
 * THE PRELIMINARY NAME — what the system currently believes the wine is
 * called, rendered AS A NAME (one line, name-sized type) with each
 * fragment's role labelled beneath it, updating live as any field changes.
 *
 * Layout reasoning (operator brief): the operator's question is "is this
 * the right name yet?", so the answer must read as a wine name, not as a
 * form echo — and it sits directly above the fields so cause (an edit) and
 * effect (the name) share one glance. Three states per slot, because
 * absence is information: a value (plain), a pending read (warning italic,
 * "(pending)" — what the name WOULD become, visibly not yet real), and a
 * missing required part (dashed "producer?" — what still blocks the name).
 */
const SEGMENT_LABELS = { producer: 'producer', grape: 'grape', region: 'region', specialName: 'cuvée', vintage: 'vintage' } as const;

interface NameSegment {
  role: keyof typeof SEGMENT_LABELS;
  text: string;
  state: 'value' | 'pending' | 'missing';
}

const NamePreview: React.FC<{ form: ReturnType<typeof useWineIdentityForm> }> = ({ form }) => {
  const segs: NameSegment[] = [];
  if (form.producer) segs.push({ role: 'producer', text: form.producer.fullName || form.producer.name, state: 'value' });
  else if (form.pendingProducer) segs.push({ role: 'producer', text: form.pendingProducer, state: 'pending' });
  else segs.push({ role: 'producer', text: 'producer?', state: 'missing' });

  for (const g of form.labelGrapes) segs.push({ role: 'grape', text: g.primaryName, state: 'value' });
  for (const g of form.pendingGrapes) segs.push({ role: 'grape', text: g, state: 'pending' });

  if (form.region) segs.push({ role: 'region', text: form.region.name, state: 'value' });
  else if (form.pendingRegion) segs.push({ role: 'region', text: form.pendingRegion, state: 'pending' });
  else segs.push({ role: 'region', text: 'region?', state: 'missing' });

  if (form.specialName.trim()) segs.push({ role: 'specialName', text: form.specialName.trim(), state: 'value' });
  if (form.vintageInput.trim()) segs.push({ role: 'vintage', text: form.vintageInput.trim(), state: 'value' });

  return (
    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        Preliminary name — updates as you edit
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
        {segs.map((seg, i) => (
          <Box key={`${seg.role}-${i}`} sx={{ textAlign: 'left' }}>
            <Typography
              variant="h6"
              component="span"
              sx={{
                fontWeight: seg.state === 'value' ? 600 : 400,
                ...(seg.state === 'pending' && { color: 'warning.main', fontStyle: 'italic' }),
                ...(seg.state === 'missing' && {
                  color: 'text.disabled',
                  borderBottom: '2px dashed',
                  borderColor: 'warning.light',
                }),
              }}
            >
              {seg.text}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
              {SEGMENT_LABELS[seg.role]}{seg.state === 'pending' ? ' (pending)' : ''}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Item 4 — one plain sentence, before the button: whether one thing or
 * three are about to be created. Entities minted during this session via
 * the Create action say (new); picked ones say (existing).
 */
const WillHappenLine: React.FC<{ form: ReturnType<typeof useWineIdentityForm> }> = ({ form }) => {
  const tag = (o: unknown) => ((o as MintTagged).__mintedNow ? 'new' : 'existing');
  const parts: string[] = [];
  if (form.producer) parts.push(`producer ${form.producer.fullName || form.producer.name} (${tag(form.producer)})`);
  if (form.region) parts.push(`region ${form.region.name} (${tag(form.region)})`);
  for (const g of form.labelGrapes) parts.push(`grape ${g.primaryName} (${tag(g)})`);
  const preview = buildDisplayNamePreview(form.producer, form.region, form.labelGrapes, form.specialName);
  if (preview) parts.push(`wine name “${preview}”`);
  if (form.vintageInput.trim()) parts.push(`wine ${form.vintageInput.trim()}`);

  // Pending reads are NOT part of what will be created — they are reads
  // still awaiting a choice, and the line must not pretend otherwise.
  const pend: string[] = [];
  if (!form.producer && form.pendingProducer) pend.push(`producer “${form.pendingProducer}”`);
  if (!form.region && form.pendingRegion) pend.push(`region “${form.pendingRegion}”`);
  for (const g of form.pendingGrapes) pend.push(`grape “${g}”`);

  if (parts.length === 0 && pend.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      {parts.length > 0 && (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Creates: {parts.join(' · ')}
        </Typography>
      )}
      {pend.length > 0 && (
        <Typography variant="body2" color="warning.main">
          Pending (not included until chosen or created): {pend.join(' · ')}
        </Typography>
      )}
    </Box>
  );
};

interface MintAsk {
  kind: 'producer' | 'region' | 'grape';
  name: string;
  prefill?: { country?: string | null; parent?: string | null };
  resolve: (extras: Record<string, string> | null) => void;
}

/**
 * B-4 — a one-tap mint is not a review, and it is not context-free either:
 * a producer needs its country said (UNKNOWN out loud if so), a region
 * needs its parent (a tap cannot invent a root), a grape needs its colour.
 * The dialog collects exactly that, prefilled from the web probe when one
 * ran. Everything minted here lands needsReview:true server-side.
 */
/** A question, not a refusal — see producerMintGuard's strip grade. */
interface ProducerChoicePayload {
  code: string;
  message: string;
  proposedName: string;
  existingProducerId: string;
  existingProducerName: string;
  options: { action: 'useExisting' | 'createDistinct'; label: string }[];
  because: string;
}

interface ProducerChoiceAsk {
  payload: ProducerChoicePayload;
  resolve: (answer: 'useExisting' | 'createDistinct' | null) => void;
}

/**
 * 'Bodegas Bellavista' already exists — same producer, or a different one?
 *
 * Both options are NAMED. The operator is choosing between two concrete
 * outcomes, not being told no and left to guess what would satisfy the
 * system.
 */
const ProducerChoiceDialog: React.FC<{ ask: ProducerChoiceAsk | null }> = ({ ask }) => {
  if (!ask) return null;
  const { payload } = ask;
  const label = (action: 'useExisting' | 'createDistinct', fallback: string) =>
    payload.options?.find((o) => o.action === action)?.label ?? fallback;
  return (
    <Dialog open onClose={() => ask.resolve(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{payload.message}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {payload.because}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Estates often share a name across countries. Nothing we hold can tell these apart, so
          this is your call.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => ask.resolve(null)}>Cancel</Button>
        <Button onClick={() => ask.resolve('useExisting')}>
          {label('useExisting', `Use ${payload.existingProducerName}`)}
        </Button>
        <Button variant="contained" onClick={() => ask.resolve('createDistinct')}>
          {label('createDistinct', `Create ${payload.proposedName}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MintExtrasDialog: React.FC<{ ask: MintAsk | null }> = ({ ask }) => {
  const [country, setCountry] = useState('');
  const [grapeColor, setGrapeColor] = useState('');
  const [parent, setParent] = useState<RegionOption | null>(null);
  const [parentOptions, setParentOptions] = useState<RegionOption[]>([]);
  const searchParents = useMemo(() => debounce(async (q: string) => {
    if (q.trim().length >= 2) setParentOptions(await wineNameService.searchRegions(q));
  }, 300), []);
  useEffect(() => {
    setCountry(ask?.prefill?.country ?? '');
    setGrapeColor('');
    setParent(null);
    setParentOptions([]);
    if (ask?.kind === 'region' && ask.prefill?.parent) searchParents(ask.prefill.parent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask]);
  if (!ask) return null;
  const canCreate =
    ask.kind === 'producer' ? !!country.trim() :
    ask.kind === 'region' ? !!parent :
    !!grapeColor;
  return (
    <Dialog open onClose={() => ask.resolve(null)} maxWidth="xs" fullWidth>
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          Create {ask.kind} “{ask.name}”
        </Typography>
        {ask.kind === 'producer' && (
          <TextField
            fullWidth size="small" label="Country" value={country} autoFocus
            onChange={(e) => setCountry(e.target.value)}
            helperText="Required — type UNKNOWN explicitly if it is. The mint queues for review either way."
          />
        )}
        {ask.kind === 'region' && (
          <Autocomplete<RegionOption>
            size="small"
            options={parentOptions}
            value={parent}
            getOptionLabel={(o) => o.hierarchyNames?.length ? `${o.name} (${o.hierarchyNames.join(' / ')})` : o.name}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            onInputChange={(_, v) => searchParents(v)}
            onChange={(_, v) => setParent(v)}
            renderInput={(params) => (
              <TextField {...params} autoFocus label="Parent region"
                helperText="Required — a console tap cannot create a root region." />
            )}
          />
        )}
        {ask.kind === 'grape' && (
          <TextField
            select fullWidth size="small" label="Grape colour" value={grapeColor} autoFocus
            onChange={(e) => setGrapeColor(e.target.value)}
            helperText="Required — no more default-red grapes."
          >
            <MenuItem value="RED">Red</MenuItem>
            <MenuItem value="WHITE">White</MenuItem>
            <MenuItem value="PINK_ROSE">Pink / Rosé</MenuItem>
          </TextField>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={() => ask.resolve(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disabled={!canCreate} sx={{ textTransform: 'none' }}
            onClick={() => ask.resolve(
              ask.kind === 'producer' ? { country: country.trim() }
                : ask.kind === 'region' ? { parentRegionId: parent!._id }
                : { grapeColor },
            )}
          >
            Create (queues for review)
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

const DecisionPanel: React.FC<DecisionPanelProps> = ({ scan, onDecided, canonicalUse, candidateUse }) => {
  const read = scan.visionRead ?? {};

  const [action, setAction] = useState<ActionKind>('approve_new');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // M-6: a 409 that rewired the form (flipped the action, preselected a
  // target) must NOT leave the primary button armed — the operator reviews
  // what the rewire did before anything can fire again.
  const [disarmed, setDisarmed] = useState(false);

  // ââ the familiar flow: the app's normal Add Wine form, pre-filled âââââââââ
  // entityOps = the console's GUARDED find-or-create: search first, explicit
  // Create action, and a fold-duplicate returns the survivor preselected.
  const [mintAsk, setMintAsk] = useState<MintAsk | null>(null);
  const mintPrefill = useRef<{ country?: string | null; parent?: string | null }>({});
  const askExtras = useCallback((kind: MintAsk['kind'], name: string) =>
    new Promise<Record<string, string> | null>((resolve) => {
      setMintAsk({ kind, name, prefill: mintPrefill.current, resolve: (extras) => { setMintAsk(null); resolve(extras); } });
    }), []);

  /**
   * A strip-grade match ('Bellavista' vs 'Bodegas Bellavista') comes back as
   * 409 PRODUCER_CHOICE_REQUIRED. It is a question, not a failure: show both
   * named options and let the operator answer. Answering "different" retries
   * with the explicit confirmation the API requires.
   */
  const [producerChoice, setProducerChoice] = useState<ProducerChoiceAsk | null>(null);
  const askProducerChoice = useCallback((payload: ProducerChoicePayload) =>
    new Promise<'useExisting' | 'createDistinct' | null>((resolve) => {
      setProducerChoice({ payload, resolve: (answer) => { setProducerChoice(null); resolve(answer); } });
    }), []);

  const entityOps: EntityOps = useMemo(() => ({
    createProducer: async (name) => {
      const extras = await askExtras('producer', name);
      if (!extras) throw Object.assign(new Error('cancelled'), { cancelled: true });
      const call = (confirmDistinctProducer?: boolean) => labelScanReviewService.findOrCreate({
        kind: 'producer', name, scanId: scan._id, ...extras,
        ...(confirmDistinctProducer ? { confirmDistinctProducer } : {}),
      });
      let r;
      try {
        r = await call();
      } catch (err: any) {
        const body = err?.response?.data;
        if (body?.code !== 'PRODUCER_CHOICE_REQUIRED') throw err;
        const answer = await askProducerChoice(body as ProducerChoicePayload);
        if (!answer) throw Object.assign(new Error('cancelled'), { cancelled: true });
        if (answer === 'useExisting') {
          return {
            _id: body.existingProducerId, name: body.existingProducerName,
            fullName: body.existingProducerName, needsReview: false, existed: true,
          };
        }
        r = await call(true);
      }
      const e = r.entity;
      return { _id: e._id, name: e.name, fullName: e.fullName, needsReview: e.needsReview, existed: !r.created };
    },
    createRegion: async (name) => {
      const extras = await askExtras('region', name);
      if (!extras) throw Object.assign(new Error('cancelled'), { cancelled: true });
      const r = await labelScanReviewService.findOrCreate({ kind: 'region', name, scanId: scan._id, ...extras });
      const e = r.entity;
      return { _id: e._id, name: e.name, hierarchyNames: e.hierarchyNames, needsReview: e.needsReview, existed: !r.created };
    },
    createGrape: async (name) => {
      const extras = await askExtras('grape', name);
      if (!extras) throw Object.assign(new Error('cancelled'), { cancelled: true });
      const r = await labelScanReviewService.findOrCreate({ kind: 'grape', name, scanId: scan._id, ...extras });
      const e = r.entity;
      return { _id: e._id, primaryName: e.primaryName, color: e.color, needsReview: e.needsReview, existed: !r.created };
    },
  }), [scan._id, askExtras, askProducerChoice]);

  // Identity fields receive the NORMALIZED display form (title case, accents
  // preserved, ordinals lowercased — see displayCase.ts for the rule); the
  // verbatim transcription stays visible in "The machine's reading" above.
  const prefill = useMemo(() => ({
    producerQuery: normalizeDisplayCase(String(read.producer?.text ?? '')),
    regionQuery: normalizeDisplayCase(String(read.appellation?.text ?? '')),
    grapeQueries: (read.labelGrapes?.values ?? []).map((g) => normalizeDisplayCase(String(g))),
    specialName: normalizeDisplayCase(String(read.specialName?.text ?? '')),
    vintageInput: read.vintage?.value != null ? String(read.vintage.value)
      : read.vintage?.vintageType === 'non_vintage' ? 'NV' : '',
    autoResolve: true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [scan._id]);

  const form = useWineIdentityForm({ entityOps, prefillKey: scan._id, prefill });

  // P5d throughput: tapping a candidate card preselects it as the merge
  // target — the operator's most common act becomes one tap.
  const appliedCandidate = useRef(0);
  useEffect(() => {
    if (!candidateUse || candidateUse.n === appliedCandidate.current) return;
    appliedCandidate.current = candidateUse.n;
    pickMergeTargetById(candidateUse.wineNameId, candidateUse.display);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateUse]);

  // P5d throughput: the wine's colour is usually the grape's — seed it
  // once when empty, from the first resolved grape; the operator can
  // always override, and a seeded value never overwrites a chosen one.
  const colourSeeded = useRef(false);
  useEffect(() => {
    if (colourSeeded.current || form.color || form.labelGrapes.length === 0) return;
    const gc = form.labelGrapes[0]?.color;
    const seed = gc === 'RED' ? 'RED' : gc === 'WHITE' ? 'WHITE' : gc === 'PINK_ROSE' ? 'ROSE' : null;
    if (seed) { form.setColor(seed as WineColor); colourSeeded.current = true; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.labelGrapes, form.color]);

  // Item 6: a verification claim's canonical spelling, applied by one tap up
  // in the testimony section. Producer/region prefills re-fire the search so
  // the picker lands on the canonical entity when the store knows it.
  const appliedCanonical = useRef(0);
  useEffect(() => {
    if (!canonicalUse || canonicalUse.n === appliedCanonical.current) return;
    appliedCanonical.current = canonicalUse.n;
    setAction('approve_new');
    const v = canonicalUse.value;
    // M-6: seeding the INPUT is not enough — an unselected Autocomplete
    // clears its text on blur (the region bug the operator hit). The
    // canonical spelling now SELECTS its fold-exact entity when the store
    // knows it, and only falls back to seeded text when it does not.
    const foldEq = (a: string, b: string) =>
      a.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
      === b.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    if (canonicalUse.slot === 'producer') {
      form.setProducer(null);
      form.setProducerInputValue(v);
      wineNameService.searchProducers(v).then((opts) => {
        form.setProducerOptions(opts);
        const hit = opts.find((o) => foldEq(o.fullName || o.name, v) || foldEq(o.name, v));
        if (hit) form.setProducer(hit);
      });
    } else if (canonicalUse.slot === 'appellation' || canonicalUse.slot === 'region') {
      form.setRegionInputValue(v);
      wineNameService.searchRegions(v).then((opts) => {
        form.setRegionOptions(opts);
        const hit = opts.find((o) => foldEq(o.name, v));
        if (hit) form.setRegion(hit);
      });
    } else if (canonicalUse.slot === 'specialName') {
      form.setSpecialName(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalUse]);

  // ââ merge picker: always available, always populated ââââââââââââââââââââââ
  const [mergeOptions, setMergeOptions] = useState<MergeCandidate[]>([]);
  const [mergeTarget, setMergeTarget] = useState<MergeCandidate | null>(null);
  const [loadingMerge, setLoadingMerge] = useState(false);
  const [mergeSpecialName, setMergeSpecialName] = useState('');

  const searchMerge = useMemo(() => debounce(async (q: string) => {
    setLoadingMerge(true);
    try {
      const r = await labelScanReviewService.mergeCandidates(scan._id, q || undefined);
      setMergeOptions(r.data);
    } finally { setLoadingMerge(false); }
  }, 300), [scan._id]);

  useEffect(() => {
    // Preload ranked by similarity to the label reading before anyone types.
    let alive = true;
    setLoadingMerge(true);
    labelScanReviewService.mergeCandidates(scan._id)
      .then((r) => { if (alive) setMergeOptions(r.data); })
      .finally(() => { if (alive) setLoadingMerge(false); });
    return () => { alive = false; };
  }, [scan._id]);

  // reject / rescan
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  // The rewire disarm lifts on the operator's next deliberate act: changing
  // the action, the merge target, or the producer selection.
  const firstDisarm = useRef(true);
  useEffect(() => {
    if (firstDisarm.current) { firstDisarm.current = false; return; }
    setDisarmed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, mergeTarget, form.producer]);

  const pickMergeTargetById = (id: string, displayName: string) => {
    setAction('merge');
    const opt = { _id: id, displayName, similarity: 1 };
    setMergeOptions((prev) => prev.some((o) => o._id === id) ? prev : [opt, ...prev]);
    setMergeTarget(opt);
  };

  const [confirmAsk, setConfirmAsk] = useState<string | null>(null);

  const submit = async () => {
    // M-6: merge and reject are the two irreversible-feeling acts — merge
    // renames venue items retroactively, reject is read by the venue. One
    // plain confirmation, stating the consequence, before either fires.
    if (confirmAsk === null && (action === 'merge' || action === 'reject')) {
      if (action === 'merge' && mergeTarget) {
        setConfirmAsk(mergeSpecialName.trim()
          ? `Merge into “${mergeTarget.displayName}” and correct the cuvée to “${mergeSpecialName.trim()}” — every venue item using this wine renames retroactively.`
          : `Merge this scan into “${mergeTarget.displayName}”.`);
        return;
      }
      if (action === 'reject') {
        setConfirmAsk(`Reject this scan — the venue will read: “${reason.trim()}”.`);
        return;
      }
    }
    setConfirmAsk(null);
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      let body;
      if (action === 'approve_new') {
        if (!form.validate({ requireVintage: false, requireRegion: false })) { setSubmitting(false); return; }
        const parsedVintage = form.vintageInput.trim() ? parseVintageInput(form.vintageInput) : null;
        body = {
          action: 'approve_new' as const,
          fields: {
            producerId: form.producer!._id,
            ...(form.region ? { regionId: form.region._id } : {}),
            ...(form.labelGrapes.length ? { grapeIds: form.labelGrapes.map((g) => g._id) } : {}),
            ...(form.specialName.trim() ? { specialName: form.specialName.trim() } : {}),
            color: form.color as WineColor,
            type: form.wineType,
            ...(form.subType ? { subType: form.subType } : {}),
            ...(parsedVintage?.valid ? { vintage: { vintageType: parsedVintage.vintageType, vintage: parsedVintage.vintage } } : {}),
          },
        };
      } else if (action === 'merge') {
        if (!mergeTarget) { setError('Pick the wine to merge into.'); setSubmitting(false); return; }
        body = {
          action: 'merge' as const,
          wineNameId: mergeTarget._id,
          ...(mergeSpecialName.trim() ? { update: { specialName: mergeSpecialName.trim() } } : {}),
        };
      } else if (action === 'reject') {
        body = { action: 'reject' as const, reason: reason.trim() };
      } else {
        body = { action: 'rescan' as const, ...(note.trim() ? { note: note.trim() } : {}) };
      }
      const res = await labelScanReviewService.decide(scan._id, body);
      if (res.decided) {
        const cascade = res.cascade ? ` Cascade: ${JSON.stringify(res.cascade)}` : '';
        setResult(`Decision recorded: ${res.action}.${res.displayName ? ` Created “${res.displayName}”.` : ''}${cascade}`);
        onDecided();
      } else {
        setResult(res.note ?? 'Recorded.');
      }
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'PRODUCER_EXISTS') {
        setDisarmed(true);
        setError(`A producer matching this name already exists as “${data.existingProducerName}” — it has been selected.`);
        if (data.existingProducerId) {
          const opt = { _id: data.existingProducerId, name: data.existingProducerName };
          form.setProducer(opt);
          form.setProducerOptions([opt]);
        }
      } else if (data?.code === 'IDENTITY_EXISTS') {
        setDisarmed(true);
        setError(`This identity already exists as “${data.existingDisplayName}” — switched to merge with it selected.`);
        if (data.existingWineNameId) pickMergeTargetById(data.existingWineNameId, data.existingDisplayName ?? 'existing wine');
      } else if (data?.code === 'DISPLAY_NAME_CONFLICT') {
        setError('The recomposed display name collides with existing venue items — nothing was changed.');
      } else {
        setError(data?.message || e?.message || 'The decision failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    action === 'approve_new' ? true :          // validate() speaks per-field on submit
    action === 'merge' ? !!mergeTarget :
    action === 'reject' ? !!reason.trim() :
    true;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>The decision</Typography>
      <RadioGroup row value={action} onChange={(e) => setAction(e.target.value as ActionKind)} sx={{ mb: 1 }}>
        <FormControlLabel value="approve_new" control={<Radio size="small" />} label="Save as new wine" />
        <FormControlLabel value="merge" control={<Radio size="small" />} label="This is an existing wine" />
        <FormControlLabel value="reject" control={<Radio size="small" />} label="Reject" />
        <FormControlLabel value="rescan" control={<Radio size="small" />} label="Request rescan" />
      </RadioGroup>

      {action === 'approve_new' && (
        <>
          {form.existingNotice && <Alert severity="info" sx={{ mb: 1 }}>{form.existingNotice}</Alert>}
          {form.opError && <Alert severity="error" sx={{ mb: 1 }}>{form.opError}</Alert>}
          {form.wineNameDuplicate && (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
              action={(
                <Button
                  size="small" color="inherit"
                  onClick={() => pickMergeTargetById(form.wineNameDuplicate!._id, form.wineNameDuplicate!.displayName)}
                  sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Merge into it
                </Button>
              )}
            >
              This exact wine name already exists: <strong>{form.wineNameDuplicate.displayName}</strong>.
            </Alert>
          )}
          {/* The app’s normal Add Wine form, pre-filled from the label. Vintage
              is optional here — an NV or unreadable-vintage approval still
              lands as a winename alone. */}
          {/* What the system currently believes the wine is called — live. */}
          <NamePreview form={form} />

          {/* Every read pre-fills its field (item 2): matched entities are
              selected; unmatched reads surface as pending chips, each with a
              one-tap guarded Create and the Ask-the-web probe attached. */}
          <WineIdentityFields
            form={form}
            vintageRequired={false}
            regionRequired={false}
            pendingExtras={(kind, text) => (
              <AskWebPanel
                scanId={scan._id}
                slot={kind}
                name={text}
                onCreateWithEvidence={(name, hints) => {
                  mintPrefill.current = { country: hints?.country ?? null, parent: hints?.parent ?? null };
                  form.resolvePending(kind, name);
                }}
              />
            )}
          />

          {/* Item 4: say what will happen, before it does. */}
          <WillHappenLine form={form} />
        </>
      )}

      {action === 'merge' && (
        <Stack spacing={1.5}>
          <Autocomplete<MergeCandidate>
            options={mergeOptions}
            value={mergeTarget}
            loading={loadingMerge}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            getOptionLabel={(o) => o.displayName}
            filterOptions={(x) => x}                      // ranking is the server's
            onInputChange={(_, v, reasonKind) => { if (reasonKind === 'input') searchMerge(v); }}
            onChange={(_, v) => setMergeTarget(v)}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="body2">{option.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    similarity {option.similarity.toFixed(2)}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Merge into (ranked by similarity to the label)"
                helperText="Preloaded from the reading — type to search the whole store"
              />
            )}
            sx={{ maxWidth: 560 }}
          />
          <TextField
            size="small"
            sx={{ maxWidth: 420 }}
            label="Correct the cuvée / special name (optional)"
            value={mergeSpecialName}
            onChange={(e) => setMergeSpecialName(e.target.value)}
            helperText="Display names are composed from the identity parts and cascade to every venue item using this wine — producer spelling is corrected on the producer record itself."
          />
          {mergeTarget && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Merges into “{mergeTarget.displayName}” — creates nothing new
              {mergeSpecialName.trim() ? `; corrects the cuvée to “${mergeSpecialName.trim()}” and renames every venue item using this wine` : ''}.
            </Typography>
          )}
        </Stack>
      )}

      {action === 'reject' && (
        <TextField
          fullWidth multiline minRows={2} size="small"
          label="Reason (the venue will read this)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}

      {action === 'rescan' && (
        <TextField
          fullWidth size="small"
          label="What should the venue photograph differently? (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      )}

      <MintExtrasDialog ask={mintAsk} />
      <ProducerChoiceDialog ask={producerChoice} />

      <Dialog open={confirmAsk !== null} onClose={() => setConfirmAsk(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2.5 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>{confirmAsk}</Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setConfirmAsk(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={submit} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Confirm
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {result && <Alert severity="success" sx={{ mt: 2 }}>{result}</Alert>}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!canSubmit || submitting || disarmed} onClick={submit}>
          {submitting ? 'Recording…' : action === 'approve_new' ? 'Save wine' : 'Record decision'}
        </Button>
        {disarmed && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
            The form was rewired by the last response — review it, then change anything to re-arm.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// ── The page ──────────────────────────────────────────────────────────────────

const LabelScanReviewDetail: React.FC = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const [scan, setScan] = useState<ScanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canonicalUse, setCanonicalUse] = useState<CanonicalUse | null>(null);
  const [candidateUse, setCandidateUse] = useState<CandidateUse | null>(null);
  const [claimedByOther, setClaimedByOther] = useState<string | null>(null);
  const navigateNext = useCallback(async () => {
    // P5d throughput: auto-advance — the queue is worked, not browsed.
    // Oldest-first order is the queue's own; we jump to the first still-
    // undecided, unclaimed scan that is not this one, else back to the list.
    try {
      const q = await labelScanReviewService.getQueue({ limit: 50 });
      const next = q.data.find((r) => r._id !== scanId && !r.claimedBy);
      if (next) navigate(`/admin/governance/label-scans/${next._id}`);
      else navigate('/admin/governance/label-scans');
    } catch {
      navigate('/admin/governance/label-scans');
    }
  }, [scanId, navigate]);

  // P5d: claim on open (TTL server-side). A 409 renders the read-only view.
  useEffect(() => {
    if (!scanId) return;
    setClaimedByOther(null);
    labelScanReviewService.claim(scanId).catch((e) => {
      if (e?.response?.status === 409) {
        setClaimedByOther(e.response.data?.claim?.actor?.email ?? 'another reviewer');
      }
    });
  }, [scanId]);

  const load = useCallback(async () => {
    if (!scanId) return;
    setLoading(true);
    try {
      setScan(await labelScanReviewService.getScan(scanId));
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load the scan.');
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !scan) return <Box sx={{ p: 3 }}><Alert severity="error">{error ?? 'Not found.'}</Alert></Box>;

  const candidates = scan.resolution?.candidates ?? [];

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/governance/label-scans')}>Queue</Button>
        <Typography variant="h5">Scan review</Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(scan.createdAt).toLocaleString()} · venue {scan.venueId}
          {scan.costUsd != null && ` · $${scan.costUsd.toFixed(4)}`}
        </Typography>
      </Stack>

      {scan.review && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Decided: <b>{scan.review.action}</b> by {scan.review.actor?.email ?? 'unknown'}
          {scan.review.at ? ` at ${new Date(String(scan.review.at)).toLocaleString()}` : ''}
          {scan.review.reason ? ` — “${scan.review.reason}”` : ''}
          {scan.review.cascade ? ` · cascade ${JSON.stringify(scan.review.cascade)}` : ''}
        </Alert>
      )}
      {scan.rescanRequested && !scan.review && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A rescan was requested{scan.rescanRequested.note ? `: “${scan.rescanRequested.note}”` : ''}. The scan stays reviewable.
        </Alert>
      )}

      <Stack spacing={2}>
        <Photos scan={scan} />
        <MachineReading scan={scan} />

        {/* P5b: the humans' corrections. Still renders NOTHING when absent —
            the coupling this section was designed around. */}
        <StaffCorrections scan={scan} />

        <WorldTestimony
          scan={scan}
          onUseCanonical={(slot, value, sourceUrl) =>
            setCanonicalUse((prev) => ({ slot, value, sourceUrl, n: (prev?.n ?? 0) + 1 }))}
        />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>The candidates</Typography>
          {candidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No canonical entity came close — the reading proposes a new identity.
            </Typography>
          ) : (
            <Stack spacing={1}>{candidates.map((c, i) => (
              <CandidateCard key={i} c={c}
                onPick={(wineNameId, display) =>
                  setCandidateUse((prev) => ({ wineNameId, display, n: (prev?.n ?? 0) + 1 }))} />
            ))}</Stack>
          )}
        </Paper>

        {claimedByOther && (
          <Alert severity="warning">
            Being reviewed by <b>{claimedByOther}</b> right now — this view is read-only until their claim expires.
          </Alert>
        )}
        {!scan.review && !claimedByOther && (
          <DecisionPanel scan={scan} onDecided={navigateNext} canonicalUse={canonicalUse} candidateUse={candidateUse} />
        )}
      </Stack>
      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default LabelScanReviewDetail;
